import { TripAddableItem } from "@/types/add-to-trip"
import { Collection } from "@/types/collection"
import { Inspiration } from "@/types/inspiration"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { AddToTripTrackingContext } from "@/components/add-to-trip-button"
import type { CustomAgentDialogMode } from "@/components/custom-agent-edit-dialog"
import { AmplitudeEmptyValue } from "../analytics/amplitude-types"

interface AddToTripDialogState {
  open: boolean
  item?: TripAddableItem
  trackingContext: AddToTripTrackingContext
}

interface AddToMediaDialogState {
  open: boolean
  dest?: Inspiration | Collection
}

export type CustomAgentEditDialogSource = "home" | "agents" | "report"

interface CustomAgentEditDialogState {
  mode?: CustomAgentDialogMode
  source: CustomAgentEditDialogSource
}

interface DialogSliceState {
  dialogOpened: boolean
  sheetOpened: boolean
  dialogInfoOpen: boolean
  addToTripDialog: AddToTripDialogState
  addToMediaDialog: AddToMediaDialogState
  customAgentEditDialog: CustomAgentEditDialogState
}

export const dialogSliceInitialState: DialogSliceState = {
  dialogOpened: false,
  sheetOpened: false,
  dialogInfoOpen: false,
  addToTripDialog: {
    open: false,
    trackingContext: AmplitudeEmptyValue,
  },
  addToMediaDialog: {
    open: false,
  },
  customAgentEditDialog: {
    source: "agents",
  },
}

const dialogSlice = createSlice({
  name: "dialogSlice",
  initialState: dialogSliceInitialState,
  reducers: {
    setDialogOpened: (state) => {
      state.dialogOpened = true
    },
    setDialogClosed: (state) => {
      state.dialogOpened = false
    },
    setSheetOpened: (state) => {
      state.sheetOpened = true
    },
    setSheetClosed: (state) => {
      state.sheetOpened = false
    },
    openDialogInfo: (state) => {
      state.dialogInfoOpen = true
    },
    closeDialogInfo: (state) => {
      state.dialogInfoOpen = false
    },
    openAddToTripDialog: (
      state,
      action: PayloadAction<{
        item: TripAddableItem
        trackingContext: AddToTripTrackingContext
      }>
    ) => {
      state.addToTripDialog.open = true
      state.addToTripDialog.item = action.payload.item
      state.addToTripDialog.trackingContext = action.payload.trackingContext
    },
    closeAddToTripDialog: (state) => {
      state.addToTripDialog.open = false
      state.addToTripDialog.item = undefined
    },
    openAddToMediaDialog: (
      state,
      action: PayloadAction<Inspiration | Collection>
    ) => {
      state.addToMediaDialog.open = true
      state.addToMediaDialog.dest = action.payload
    },
    closeAddToMediaDialog: (state) => {
      state.addToMediaDialog.open = false
      state.addToMediaDialog.dest = undefined
    },
    openCustomAgentEditDialog: (
      state,
      action: PayloadAction<{
        mode: CustomAgentDialogMode
        source: CustomAgentEditDialogSource
      }>
    ) => {
      state.customAgentEditDialog.mode = action.payload.mode
      state.customAgentEditDialog.source = action.payload.source
    },
    closeCustomAgentEditDialog: (state) => {
      state.customAgentEditDialog.mode = undefined
    },
  },
  selectors: {
    selectHasOpenDialog: (state) => state.dialogOpened,
    selectHasOpenSheet: (state) => state.sheetOpened,
    selectIsDialogInfoOpen: (state) => state.dialogInfoOpen,
    selectAddToTripDialogOpen: (state) => state.addToTripDialog.open,
    selectAddToTripDialogItem: (state) => state.addToTripDialog.item,
    selectAddToTripDialogTrackingContext: (state) =>
      state.addToTripDialog.trackingContext,
    selectAddToMediaDialogOpen: (state) => state.addToMediaDialog.open,
    selectAddToMediaDialogDest: (state) => state.addToMediaDialog.dest,
    selectCustomAgentEditDialogOpen: (state) =>
      state.customAgentEditDialog.mode !== undefined,
    selectCustomAgentEditDialogMode: (state) =>
      state.customAgentEditDialog.mode,
    selectCustomAgentEditDialogSource: (state) =>
      state.customAgentEditDialog.source,
  },
})

export const {
  setDialogOpened,
  setDialogClosed,
  setSheetOpened,
  setSheetClosed,
  openDialogInfo,
  closeDialogInfo,
  openAddToTripDialog,
  closeAddToTripDialog,
  openAddToMediaDialog,
  closeAddToMediaDialog,
  openCustomAgentEditDialog,
  closeCustomAgentEditDialog,
} = dialogSlice.actions

export const {
  selectHasOpenDialog,
  selectHasOpenSheet,
  selectIsDialogInfoOpen,
  selectAddToTripDialogOpen,
  selectAddToTripDialogItem,
  selectAddToTripDialogTrackingContext,
  selectAddToMediaDialogOpen,
  selectAddToMediaDialogDest,
  selectCustomAgentEditDialogOpen,
  selectCustomAgentEditDialogMode,
  selectCustomAgentEditDialogSource,
} = dialogSlice.selectors

export default dialogSlice.reducer
